// TeamResponsesCard.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { Users, MessageCircle, ChevronUp, ChevronDown, Star, Clock } from 'lucide-react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Comment, TeamResponseCard } from './TeamComponents';

const TeamResponsesCard = ({
  teamResponses = [],
  comments = [],
  reactions = {},
  currentUser,
  teamMembers = [],
  onLockToUser,
  onCommentAdd,
  onReactionAdd,
  isAdmin,
  isViewer,
  question
}) => {
  const [showAllComments, setShowAllComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [localComments, setLocalComments] = useState(comments);

  // Process and organize team responses
  const {
    officialResponse,
    userResponse,
    otherResponses
  } = useMemo(() => {
    if (!Array.isArray(teamResponses)) {
      return {
        officialResponse: null,
        userResponse: null,
        otherResponses: []
      };
    }

    const processed = teamResponses
      .filter(response => response && response.user_id)
      .map(response => {
        const member = teamMembers.find(m => m.user_id === response.user_id);
        return {
          ...response,
          userName: member?.name || response.user_name || 'Unknown User',
          isCurrentUser: response.user_id === currentUser?.id,
          isAdmin: member?.role === 'admin',
          question_type: question?.question_type,
          question_options: question?.options
        };
      });

    const official = processed.find(r => r.isOfficialResponse);
    const user = processed.find(r => r.isCurrentUser && !r.isOfficialResponse);
    const others = processed.filter(r => !r.isOfficialResponse && !r.isCurrentUser);

    return {
      officialResponse: official || null,
      userResponse: user || null,
      otherResponses: others
    };
  }, [teamResponses, teamMembers, currentUser, question]);

  const handleCommentSubmit = async (e) => {
    if ((e.type === 'submit' || (e.key === 'Enter' && !e.shiftKey)) && newComment.trim()) {
      e.preventDefault();
      const tempComment = {
        id: `temp-${Date.now()}`,
        user_id: currentUser.id,
        comment_text: newComment.trim(),
        created_at: new Date().toISOString()
      };
      setLocalComments(prev => [...prev, tempComment]);
      setNewComment('');
      await onCommentAdd(newComment.trim());
    }
  };

  const visibleComments = useMemo(() => {
    const sortedComments = [...localComments].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
    return showAllComments ? sortedComments : sortedComments.slice(-2);
  }, [localComments, showAllComments]);

  useEffect(() => {
    setLocalComments(comments);
  }, [comments]);

  const responseCount = teamResponses.length;
  const commentCount = localComments.length;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Team Collaboration</h3>
              <p className="text-sm text-gray-600">See what your team thinks</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm">
                <Users className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">{responseCount}</span>
                <span className="text-xs text-gray-500">responses</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm">
                <MessageCircle className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">{commentCount}</span>
                <span className="text-xs text-gray-500">comments</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Team Responses Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-blue-500 rounded-full"></div>
            <h4 className="text-lg font-semibold text-gray-900">Team Responses</h4>
            {responseCount > 0 && (
              <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">
                {responseCount} total
              </span>
            )}
          </div>

          {responseCount === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">No team responses yet</p>
              <p className="text-gray-400 text-xs mt-1">Be the first to share your thoughts!</p>
            </div>
          ) : (
            <div className="space-y-4">
            {/* Official Response */}
            {officialResponse && (
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                      Official Team Response
                    </span>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <TeamResponseCard
                  response={officialResponse}
                  onSetOfficial={isAdmin ? onLockToUser : undefined}
                  question={question}
                  variant="official"
                />
                  </div>
              </div>
            )}

            {/* User's Response */}
            {userResponse && (
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-green-700">Your Response</span>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <TeamResponseCard
                  response={userResponse}
                  onSetOfficial={isAdmin ? onLockToUser : undefined}
                  question={question}
                  variant="user"
                />
                  </div>
              </div>
            )}

            {/* Other Team Responses */}
            {otherResponses.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">Other Team Members</span>
                    <span className="text-xs text-gray-500">({otherResponses.length})</span>
                  </div>
                <div className="space-y-3">
                  {otherResponses.map(response => (
                      <div key={response.id || response.user_id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <TeamResponseCard
                      response={response}
                      onSetOfficial={isAdmin ? onLockToUser : undefined}
                      question={question}
                      variant="team"
                    />
                      </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Comments Section */}
        <div className="space-y-6 border-t border-gray-100 pt-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 bg-purple-500 rounded-full"></div>
              <h4 className="text-lg font-semibold text-gray-900">Discussion</h4>
              {commentCount > 0 && (
                <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full font-medium">
                  {commentCount} comments
                </span>
              )}
            </div>
            {localComments.length > 2 && (
              <button
                onClick={() => setShowAllComments(!showAllComments)}
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                {showAllComments ? (
                  <>
                    Show Recent
                    <ChevronUp className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    View All ({localComments.length})
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>

          {commentCount === 0 ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MessageCircle className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">No comments yet</p>
              <p className="text-gray-400 text-xs mt-1">Start the conversation!</p>
            </div>
          ) : (
          <div className="space-y-4">
            {visibleComments.map(comment => (
                <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
              <Comment
                comment={comment}
                teamMembers={teamMembers}
                onReaction={onReactionAdd}
                reactions={reactions}
                currentUser={currentUser}
              />
                </div>
            ))}
          </div>
          )}
          
{/* Comment Input */}
{!isViewer && (
            <div className="bg-gray-50 rounded-lg p-4">
              <form onSubmit={handleCommentSubmit} className="space-y-3">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={handleCommentSubmit}
                  placeholder="Share your thoughts with the team..."
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white shadow-sm"
                rows={3}
              />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                  Press Shift+Enter for new line
                </span>
                <button
                  type="submit"
                  disabled={!newComment.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  Add Comment
                </button>
              </div>
            </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamResponsesCard;